import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { apiFetch } from "../../api";
import type { Destination, Project } from "../../configTypes";
import {
  approveApply,
  changeSftpUsers,
  createWorkspace,
  listAgentReleases,
  listRuns,
  listWorkspaces,
  promoteAgentRelease,
  refreshSftpUsers,
  requestPlan,
  rollbackAgentRelease,
  updateWorkspace,
} from "./onsiteApi";
import { isActiveRun, runsRefetchInterval, warehouseProjectId } from "./onsiteModel";
import type {
  CreateWorkspaceInput,
  PromoteReleaseInput,
  SftpUsersChange,
  WorkspaceSettingsPatch,
} from "./types";

const keys = {
  workspaces: ["onsite", "workspaces"] as const,
  projects: ["onsite", "projects"] as const,
  warehouse: (projectId: string) => ["onsite", "warehouse", projectId] as const,
  runs: (workspaceId: string) => ["onsite", "runs", workspaceId] as const,
  releases: ["onsite", "releases"] as const,
};

export function useWorkspaces() {
  return useQuery({
    queryKey: keys.workspaces,
    queryFn: listWorkspaces,
    select: (data) => data.workspaces,
  });
}

export function useRunnerProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: () => apiFetch<Project[]>("/api/projects"),
  });
}

/** The selected runner Project's GCP project, from its BigQuery destination (null when ambiguous). */
export function useWarehouseProject(projectId: string) {
  return useQuery({
    queryKey: keys.warehouse(projectId),
    queryFn: () => apiFetch<Destination[]>(`/api/projects/${projectId}/destinations`),
    enabled: projectId !== "",
    select: warehouseProjectId,
  });
}

/**
 * Polls while a run is pending or running. When the last active run settles, the workspace list
 * is refetched too: an apply's report updates the workspace's status and outputs.
 */
export function useRuns(workspaceId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: keys.runs(workspaceId),
    queryFn: () => listRuns(workspaceId),
    select: (data) => data.runs,
    refetchInterval: (q) => runsRefetchInterval(q.state.data?.runs),
  });
  const active = query.data?.some(isActiveRun) ?? false;
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) {
      void queryClient.invalidateQueries({ queryKey: keys.workspaces });
    }
    wasActive.current = active;
  }, [active, queryClient]);
  return query;
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) => createWorkspace(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workspaces }),
  });
}

export function useUpdateWorkspace(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: WorkspaceSettingsPatch) => updateWorkspace(workspaceId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workspaces }),
  });
}

export function useRequestPlan(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => requestPlan(workspaceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.runs(workspaceId) }),
  });
}

export function useApproveApply(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planRunId: number) => approveApply(planRunId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.runs(workspaceId) }),
  });
}

/** The new run starts the runs poll; its report refreshes `facts.sftpUsers` via `useRuns`. */
function invalidateAfterSftpUsersRun(queryClient: QueryClient, workspaceId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.runs(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: keys.workspaces }),
  ]);
}

export function useChangeSftpUsers(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SftpUsersChange) => changeSftpUsers(workspaceId, body),
    onSuccess: () => invalidateAfterSftpUsersRun(queryClient, workspaceId),
  });
}

export function useAgentReleases() {
  return useQuery({ queryKey: keys.releases, queryFn: () => listAgentReleases() });
}

/** A new release moves every workspace's Current/Behind badge, so both lists are read again. */
function invalidateAfterRelease(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: keys.releases }),
    queryClient.invalidateQueries({ queryKey: keys.workspaces }),
  ]);
}

export function usePromoteAgentRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PromoteReleaseInput) => promoteAgentRelease(body),
    onSuccess: () => invalidateAfterRelease(queryClient),
  });
}

export function useRollbackAgentRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => rollbackAgentRelease(note),
    onSuccess: () => invalidateAfterRelease(queryClient),
  });
}

export function useRefreshSftpUsers(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => refreshSftpUsers(workspaceId),
    onSuccess: () => invalidateAfterSftpUsersRun(queryClient, workspaceId),
  });
}
